
import React, { useEffect, useState, useCallback, useRef } from 'react'
import styles from './styles.module.css'
import cn from 'classnames'

const MAX_VALUE = 9999
const MIN_VALUE = 0
const HOLD_DELAY = 400
const HOLD_INTERVAL = 80

export default function Counter({ label, value, setFieldValue, error, className, name, ...props }) {
  const [countValue, setCountValue] = useState(value ?? 0)
  const [isFocused, setIsFocused] = useState(false)
  const intervalRef = useRef(null)
  const timeoutRef = useRef(null)
  const didMount = useRef(false)

  // Не записываем значение в форму на первом маунте — иначе дефолт из формы
  // (например, текущий год) затирается нулём и возникает цикл с эффектом ниже.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      return
    }
    setFieldValue(name, countValue)
  }, [countValue, name, setFieldValue])

  useEffect(() => {
    if (value !== undefined && value !== countValue) {
      setCountValue(value)
    }
  }, [value])

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current)
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const labelClassName = cn(styles.label, className)
  const wrapperClassName = cn(styles.inputButton, {
    [styles.inputButtonFocused]: isFocused,
    [styles.inputButtonError]: error
  })

  const increase = useCallback(() => {
    setCountValue(prev => (prev < MAX_VALUE ? prev + 1 : prev))
  }, [])

  const decrease = useCallback(() => {
    setCountValue(prev => (prev > MIN_VALUE ? prev - 1 : prev))
  }, [])

  const handleChange = useCallback((e) => {
    const val = e.target.value.replace(/[^0-9]/g, '')
    const numVal = parseInt(val, 10)
    if (val === '') {
      setCountValue(0)
    } else if (numVal <= MAX_VALUE) {
      setCountValue(numVal)
    }
  }, [])

  const handleKeyDown = useCallback((e) => {
    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
      e.preventDefault()
    }
  }, [])

  const stopHold = useCallback(() => {
    clearInterval(intervalRef.current)
    clearTimeout(timeoutRef.current)
  }, [])

  const startHold = useCallback((action) => {
    action()
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(action, HOLD_INTERVAL)
    }, HOLD_DELAY)
  }, [])

  const isAtMin = +countValue <= MIN_VALUE
  const isAtMax = +countValue >= MAX_VALUE

  return (
    <div className={styles.counter}>
      <label className={props.name !== "folderName" ? labelClassName : styles.labelForMethodOffice}>
        <p>{label}</p>
        <div className={styles.inputWrapper}>
          <div className={wrapperClassName}>
            <input
              type="text"
              inputMode="numeric"
              name={name}
              className={cn(styles.input, name)}
              value={countValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            <div className={styles.buttons}>
              <button
                type="button"
                className={cn(styles.btn, { [styles.btnDisabled]: isAtMax })}
                onMouseDown={() => startHold(increase)}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                disabled={isAtMax}
                aria-label="Увеличить"
              >
                +
              </button>
              <button
                type="button"
                className={cn(styles.btn, { [styles.btnDisabled]: isAtMin })}
                onMouseDown={() => startHold(decrease)}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                disabled={isAtMin}
                aria-label="Уменьшить"
              >
                −
              </button>
            </div>
          </div>
          {error && <p className={styles.errorText}>{error}</p>}
        </div>
      </label>
    </div>
  )
}
